/**
 * Accounting Rules Cheat Sheet
 * 
 * This file contains rules for automatically filling journal entries
 * based on transaction types, document types, and other conditions.
 * 
 * Rules are applied when creating a new journal entry from a transaction or document.
 */

import { TransactionType } from '../../../../../../utils/enum';
import { parseCreditCardIdFromPeriod, resolveCreditCardAccountKey } from '../../../../../../utils/accountChartCreditCardHelpers';
import {
  DEFAULT_BANK_ACCOUNT_KEY,
  resolveBankAccountKey,
} from '../../../../../../utils/accountChartBankHelpers';
import { getCompanyAccountByTag } from '../../../../../../utils/accountSystemTags';
import { resolveWinningDocumentRule } from './resolveWinningDocumentRule';
import { resolveDocumentRuleAccountKey } from './documentRuleException';

/** Keep in sync with document-microservice/pascale/boletaVenta.helpers.ts */
export const BOLETA_VENTA_DOCUMENT_CODES = [35, 38, 39, 41, 48, '35', '38', '39', '41', '48'];

export const isBoletaVentaDocument = (documentCode) =>
  documentCode != null && BOLETA_VENTA_DOCUMENT_CODES.includes(documentCode);

export const resolveSalesReceivableAccount = (accounts, documentCode) => {
  if (isBoletaVentaDocument(documentCode)) {
    return (
      getCompanyAccountByTag('CLIENTES_BOLETAS', accounts) ||
      getCompanyAccountByTag('CLIENTES', accounts)
    );
  }
  return getCompanyAccountByTag('CLIENTES', accounts);
};

export const resolveSalesRevenueAccount = (accounts, documentCode) => {
  if (isBoletaVentaDocument(documentCode)) {
    return (
      getCompanyAccountByTag('INGRESOS_POR_BOLETA', accounts) ||
      getCompanyAccountByTag('INGRESOS', accounts)
    );
  }
  return getCompanyAccountByTag('INGRESOS', accounts);
};

/** Keep in sync with document-microservice/pascale/cheatsheet.logic.ts + server/src/pascale/logic/cheatsheet.logic.ts */
export const MATCH_RULE_CATCH_ALL_TYPE = '*';

/** Journal entries are always stored and displayed in CLP. */
export const JOURNAL_ENTRY_CURRENCY = 'CLP';

const positiveAbs = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.abs(n) : 0;
};

/** CLP amount for match-entry lines. */
export const resolveMatchEntryAmountClp = (match, transaction) => {
  if (!match) return 0;

  const localValue = positiveAbs(match.localValue);
  if (localValue > 0) return localValue;

  const localAmount = positiveAbs(match.localAmount);
  if (localAmount > 0) return localAmount;

  const matchCurrency = String(match.matchCurrency || 'CLP').toUpperCase();
  const amount = positiveAbs(match.amount);
  if (matchCurrency === 'CLP' && amount > 0) return amount;

  const matchedAmount = positiveAbs(match.matchedAmount ?? match.originalAmount);
  const exchangeRate = positiveAbs(match.exchangeRate);
  if (matchedAmount > 0 && exchangeRate > 0) return matchedAmount * exchangeRate;

  if (transaction) {
    const txCurrency = String(transaction.currency || 'CLP').toUpperCase();
    const amountCLP = positiveAbs(transaction.amountCLP);
    const txAmount = positiveAbs(transaction.amount);
    if (txCurrency !== 'CLP' && amountCLP > 0 && txAmount > 0 && matchedAmount > 0) {
      return (amountCLP / txAmount) * matchedAmount;
    }
    const txRate = positiveAbs(transaction.rate?.exchange ?? transaction.exchangeRate);
    if (txCurrency !== 'CLP' && matchedAmount > 0 && txRate > 0) {
      return matchedAmount * txRate;
    }
    if (txCurrency === 'CLP' && amount > 0) return amount;
  }

  if (amount > 0) return amount;
  if (matchedAmount > 0) return matchedAmount;
  return 0;
};

export const resolveDocumentEntryAmountClp = (doc) => {
  if (!doc) return 0;
  const meta = doc.meta || {};
  const ex = meta.exchange;
  if (ex?.localAmount != null && Number(ex.localAmount) > 0) {
    return positiveAbs(ex.localAmount);
  }
  if (doc.localAmount != null && Number(doc.localAmount) > 0) {
    return positiveAbs(doc.localAmount);
  }
  if (doc.localValue != null && Number(doc.localValue) > 0) {
    return positiveAbs(doc.localValue);
  }
  const currency = String(doc.currencyType || doc.currency || 'CLP').toUpperCase();
  const amount = positiveAbs(doc.amount ?? meta.amount);
  if (currency === 'CLP') return amount;
  const rate = positiveAbs(ex?.exchangeRate);
  const original = positiveAbs(ex?.originalAmount);
  if (original > 0 && rate > 0) return original * rate;
  return amount;
};

/** Keep in sync with server/src/entries/document-entry-completion.helpers.ts */
export const FACTURA_COMPRA_ELECTRONICA_DOCUMENT_CODES = [46, '46'];

export const normalizeDocumentCode = (code) =>
  code == null || code === '' ? null : String(code).trim();

export const isFacturaCompraDocumentCode = (code) => {
  const n = normalizeDocumentCode(code);
  return n === '46' || Number(n) === 46;
};

export const isFacturaCompraElectronicaReceived = (doc) => {
  if (!doc) return false;
  const meta = doc.meta || {};
  if (meta.isReceived !== true) return false;
  if (!isFacturaCompraDocumentCode(meta.documentCode)) return false;
  if (doc.documentType === 'TAX_DOCUMENT') return true;
  const type = String(meta.type || '').toLowerCase();
  return (
    type.includes('facturadecompra') ||
    String(doc.fieldKey || '').toLowerCase().includes('compras')
  );
};

export const getDocumentEmissionJournalCapAmount = (doc) => {
  if (!isFacturaCompraElectronicaReceived(doc)) return null;
  const meta = doc.meta || {};
  const net = positiveAbs(meta.netAmount);
  const taxLine = positiveAbs(meta.taxAmount) + positiveAbs(meta.commonTaxes);
  return net + taxLine;
};

export const getDocumentPayableAmountClp = (doc) => {
  if (!doc) return 0;
  const meta = doc.meta || {};
  if (meta.remaining != null && Number.isFinite(Number(meta.remaining))) {
    return positiveAbs(meta.remaining);
  }
  return positiveAbs(doc.amount ?? doc.localAmount ?? meta.netAmount ?? 0);
};

export const resolveDocumentEmissionJournalCapClp = (doc) => {
  const cap = getDocumentEmissionJournalCapAmount(doc);
  return cap != null && cap > 0 ? cap : null;
};

/** Emission total to match (journal cap for DTE 46 / consolidated honorarios basic; else document CLP amount). */
export const resolveDocumentEmissionSourceAmountClp = (doc, company = null, entryRows = null) => {
  const cap = resolveDocumentEmissionJournalCapClp(doc);
  if (cap != null) return cap;

  const honorariosConsolidated =
    isConsolidatedHonorariosEmissionEntry(entryRows) ||
    (isPascaleBasicMode(company) && isHonorariosTaxDocument(doc));
  if (honorariosConsolidated) {
    const honorariosCap = getHonorariosEmissionJournalCapAmount(doc);
    if (honorariosCap != null) return honorariosCap;
  }

  return resolveDocumentEntryAmountClp(doc);
};

export const resolveTransactionEntryAmountClp = (tx) => {
  if (!tx) return 0;
  const currency = String(tx.currency || 'CLP').toUpperCase();
  if (currency === 'CLP') {
    return positiveAbs(tx.amount ?? tx.localAmount ?? tx.localValue);
  }
  const amountCLP = positiveAbs(tx.amountCLP);
  if (amountCLP > 0) return amountCLP;
  const localValue = positiveAbs(tx.localValue);
  if (localValue > 0) return localValue;
  const rate = positiveAbs(tx.rate?.exchange ?? tx.exchangeRate);
  const amt = positiveAbs(tx.amount);
  if (rate > 0 && amt > 0) return amt * rate;
  return 0;
};

/** CLP target for transaction.entries.completed — keep in sync with server cheatsheet.logic.ts */
export const resolveTransactionEntryCompletionTargetClp = (tx) => {
  if (!tx) return 0;
  const currency = String(tx.currency || 'CLP').toUpperCase();
  if (currency !== 'CLP') {
    const matchs = Array.isArray(tx.matchs) ? tx.matchs : [];
    if (matchs.length > 0) {
      const fromMatches = matchs.reduce(
        (sum, m) => sum + resolveMatchEntryAmountClp(m, tx),
        0,
      );
      if (fromMatches > 0) return fromMatches;
    }
  }
  return resolveTransactionEntryAmountClp(tx);
};

export { resolveBankAccountKey } from '../../../../../../utils/accountChartBankHelpers';

const normalizePasswordId = (value) => {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  return s || null;
};

export const getBankAccountForTransaction = (
  transaction,
  accounts,
  passwordIdToAccountKeyMap = null,
) => {
  const key = resolveBankAccountKey(
    transaction,
    accounts,
    DEFAULT_BANK_ACCOUNT_KEY,
    passwordIdToAccountKeyMap,
  );
  return getAccountByKey(key, accounts);
};

const resolveTransactionForBankAccount = (editData) => {
  const fromSupport = editData?.support?.transactions?.[0];
  if (fromSupport) return fromSupport;
  if (normalizePasswordId(editData?.passwordId)) return editData;
  return null;
};

const MATCH_FILTER_KIND_AGENDA_ID = 'AGENDA_ID';
const MATCH_FILTER_KIND_BANK_CREDIT_PRINCIPAL = 'BANK_CREDIT_PRINCIPAL';
const MATCH_FILTER_KIND_BANK_PASSWORD_ID = 'BANK_PASSWORD_ID';
const MATCH_DOCUMENT_TYPE_CAPITAL_CUOTA = 'capitaldecuota';

function documentMetaType(document) {
  return String(document?.meta?.type ?? '').trim().toLowerCase();
}

function resolveParentCreditId(document, ctx) {
  const fromMeta = String(document?.meta?.parentCreditId ?? '').trim();
  if (fromMeta) return fromMeta;

  const installmentId = document?.meta?.creditDocumentId;
  if (!installmentId) return null;

  const docs = ctx?.supportDocuments ?? [];
  const installment = docs.find(
    (d) => String(d?.id ?? d?._id ?? '') === String(installmentId),
  );
  const identificator = installment?.meta?.identificator;
  return identificator != null && String(identificator).trim()
    ? String(identificator).trim()
    : null;
}

function matchAgendaIdsEqual(a, b) {
  const na = normalizeAgendaIdCandidate(a);
  const nb = normalizeAgendaIdCandidate(b);
  if (!na || !nb) return false;
  return na.toLowerCase() === nb.toLowerCase();
}

/** Normaliza id de agenda: string, ObjectId (toHexString), Buffer 12B, EJSON { $oid }, populate { id }. */
function normalizeAgendaIdCandidate(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const s = raw.trim();
    return s || null;
  }
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer && Buffer.isBuffer(raw) && raw.length === 12) {
    try {
      return raw.toString('hex');
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') {
    if (raw.$oid != null && String(raw.$oid).trim()) return String(raw.$oid).trim();
    if (raw.id != null && raw.id !== '') return normalizeAgendaIdCandidate(raw.id);
    if (raw._id != null && raw._id !== '') return normalizeAgendaIdCandidate(raw._id);
    if (typeof raw.toHexString === 'function') {
      try {
        return raw.toHexString();
      } catch {
        /* ignore */
      }
    }
  }
  const fallback = String(raw).trim();
  if (!fallback) return null;
  if (/^[a-fA-F0-9]{24}$/.test(fallback)) return fallback.toLowerCase();
  return fallback;
}

/** Ids de agenda en el documento (solo `agendaId` raíz; excepciones MATCH). */
export function collectAgendaIdsForMatchRule(document) {
  if (!document) return [];
  const n = normalizeAgendaIdCandidate(document.agendaId);
  return n ? [n] : [];
}

/** @deprecated usar collectAgendaIdsForMatchRule; se mantiene por compatibilidad. */
export function getAgendaIdForMatchRule(document) {
  const ids = collectAgendaIdsForMatchRule(document);
  return ids[0] ?? null;
}

function getMatchExceptionFilters(ex) {
  if (!Array.isArray(ex?.filters) || ex.filters.length === 0) return [];
  return ex.filters.map((f) => ({
    kind: String(f?.kind || '').trim(),
    agendaId: f?.agendaId,
    value: f?.value != null ? String(f.value) : undefined,
  }));
}

function matchExceptionFiltersAllSatisfied(filters, ctx) {
  if (!filters.length) return false;
  for (const f of filters) {
    const kind = String(f.kind || '').trim();
    if (kind === MATCH_FILTER_KIND_AGENDA_ID) {
      const ok = ctx.documentAgendaIdList.some((docId) => matchAgendaIdsEqual(f.agendaId, docId));
      if (!ok) return false;
    } else if (kind === MATCH_FILTER_KIND_BANK_CREDIT_PRINCIPAL) {
      if (documentMetaType(ctx.document) !== MATCH_DOCUMENT_TYPE_CAPITAL_CUOTA) return false;
      const expectedCreditId = String(f.value ?? '').trim();
      if (!expectedCreditId) return false;
      const parentCreditId = resolveParentCreditId(ctx.document, ctx);
      if (!parentCreditId || parentCreditId !== expectedCreditId) return false;
    } else if (kind === MATCH_FILTER_KIND_BANK_PASSWORD_ID) {
      if (!matchAgendaIdsEqual(f.value, ctx.transaction?.passwordId)) return false;
    } else {
      return false;
    }
  }
  return true;
}

function resolveMatchRuleAccountKey(rule, agendaIdOrIds, document, options = {}) {
  if (!rule?.accountKey) return null;
  const idList = Array.isArray(agendaIdOrIds)
    ? agendaIdOrIds.map((x) => normalizeAgendaIdCandidate(x)).filter(Boolean)
    : agendaIdOrIds != null && agendaIdOrIds !== ''
      ? (() => {
          const n = normalizeAgendaIdCandidate(agendaIdOrIds);
          return n ? [n] : [];
        })()
      : [];
  const ctx = {
    documentAgendaIdList: idList,
    document: document ?? null,
    transaction: options.transaction,
    supportDocuments: options.supportDocuments,
  };
  for (const ex of rule.exceptions || []) {
    const filters = getMatchExceptionFilters(ex);
    if (!filters.length) continue;
    if (matchExceptionFiltersAllSatisfied(filters, ctx)) {
      return ex.accountKey || rule.accountKey;
    }
  }
  return rule.accountKey;
}

/**
 * Get account from matching rule
 *
 * @param {string} transactionType - Transaction type: "SPEND" or "RECIEVED"
 * @param {string} type - Document meta type (e.g. "otrosingresos"); use "*" / catch-all when unknown
 * @param {Array} rules - Array of rules from the rules system
 * @param {Array} accounts - The account chart data from context
 * @param {string|string[]|undefined} agendaId - Uno o varios ObjectId de agenda/contraparte para excepciones MATCH
 * @returns {Object|null} - Returns the account object or null if no rule matches
 */
export const getAccountFromRule = (
  transactionType,
  type,
  rules = [],
  accounts = [],
  agendaId,
  document,
  options = {},
) => {
  if (!transactionType || !rules || rules.length === 0 || !accounts?.length) {
    return null;
  }

  const isCatchAll = !type || type === MATCH_RULE_CATCH_ALL_TYPE;
  const matchingRules = rules.filter(rule =>
    rule.ruleType === 'MATCH' &&
    rule.isEnabled !== false &&
    (isCatchAll ? !rule.type || rule.type === MATCH_RULE_CATCH_ALL_TYPE : rule.type === type) &&
    (rule.transactionType === transactionType || rule.transactionType === 'ALL')
  );

  if (matchingRules.length === 0) {
    return null;
  }

  matchingRules.sort((a, b) => (a.priority || 999) - (b.priority || 999));

  const rule = matchingRules[0];
  const accountKey = resolveMatchRuleAccountKey(rule, agendaId, document, options);
  return accountKey ? getAccountByKey(accountKey, accounts) : null;
};

const normalizeRut = (rut) => {
  if (!rut) return '';
  return String(rut)
    .trim()
    .toUpperCase()
    .replace(/\./g, '')
    .replace(/-/g, '')
    .replace(/\s+/g, '');
};

const HONORARIO_DOCUMENT_CODES = [0, 1, '0', '1'];

export const RETENCION_BH_ACCOUNT_KEY = '2.07.02.01';

export const isHonorariosTaxDocument = (document) => {
  if (!document) return false;
  const documentType = document.documentType;
  if (documentType && documentType !== 'TAX_DOCUMENT') return false;
  const documentCode = document.meta?.documentCode ?? document.documentCode;
  return documentCode != null && HONORARIO_DOCUMENT_CODES.includes(documentCode);
};

export const isPascaleBasicMode = (company) =>
  company?.isPascaleActive?.mode === 'basic' && company?.isPascaleActive?.active !== false;

/**
 * Pascale basic (Option A): Debe Gastos Honorarios (bruto) / Haber Honorarios por Pagar / Haber Retención BH.
 * Keep in sync with document-microservice/pascale/cheatsheet.logic.ts
 */
export const buildConsolidatedHonorariosJournalEntries = (document, company = null, accounts = []) => {
  if (!isHonorariosTaxDocument(document)) return null;

  const meta = document.meta || {};
  const netAmount = Number(meta.netAmount) || 0;
  const retentionAmount = Number(meta.taxAmount) || 0;
  const grossAmount = netAmount + retentionAmount;
  if (grossAmount <= 0) return null;

  const gastosHonorarios = getCompanyAccountByTag('GASTOS_HONORARIOS', accounts);
  const honorariosPorPagar = getCompanyAccountByTag('HONORARIOS_POR_PAGAR', accounts);
  const retencionBh = getCompanyAccountByTag('RETENCION_BH', accounts);

  if (!gastosHonorarios?.key || !honorariosPorPagar?.key) return null;
  if (retentionAmount > 0 && !retencionBh?.key) return null;

  const rows = [
    { ranking: 1, account: gastosHonorarios, debit: grossAmount, credit: 0 },
    { ranking: 2, account: honorariosPorPagar, debit: 0, credit: netAmount },
  ];
  if (retentionAmount > 0) {
    rows.push({ ranking: 3, account: retencionBh, debit: 0, credit: retentionAmount });
  }
  return rows;
};

export const isConsolidatedHonorariosEmissionEntry = (entryRows) =>
  (entryRows ?? []).some(
    (row) =>
      row?.account?.key === RETENCION_BH_ACCOUNT_KEY ||
      row?.accountKey === RETENCION_BH_ACCOUNT_KEY,
  );

export const getHonorariosEmissionJournalCapAmount = (doc) => {
  if (!isHonorariosTaxDocument(doc)) return null;
  const meta = doc.meta || {};
  const net = positiveAbs(meta.netAmount);
  const retention = positiveAbs(meta.taxAmount);
  if (retention > 0) return net + retention;
  return net > 0 ? net : null;
};

const CREDIT_NOTE_DOCUMENT_CODES = [61, 112, '61', '112'];

const IVA_RETENIDO_DEFAULT_KEY = '2.06.02.02';

const normalizeAccountText = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const accountKeySegmentCount = (accountKey) =>
  String(accountKey || '')
    .split('.')
    .filter(Boolean).length;

const isOtherTaxIvaRetenido = (tax) => {
  if (!tax) return false;
  if (String(tax.code ?? '').trim() === '15') return true;
  return normalizeAccountText(tax.name).includes('retenido');
};

export const isFacturaCompraWithIvaRetenido = (doc) => {
  if (!isFacturaCompraElectronicaReceived(doc)) return false;
  const otherTaxes = doc?.meta?.otherTaxes;
  if (!Array.isArray(otherTaxes) || otherTaxes.length === 0) return false;
  const otherTaxesAmount = otherTaxes.reduce(
    (sum, tax) => sum + positiveAbs(tax?.value),
    0,
  );
  return otherTaxesAmount > 0 && otherTaxes.some(isOtherTaxIvaRetenido);
};

const isIvaRetenidoNameMatch = (item) => {
  const key = normalizeAccountText(item?.key);
  const combined = `${normalizeAccountText(item?.label)} ${normalizeAccountText(item?.name)}`.trim();
  return combined.includes('iva retenido') || key === '2 06 02 02';
};

const findIvaRetenidoAccount = (accounts) => {
  if (!Array.isArray(accounts) || accounts.length === 0) return null;
  const matches = [];
  const collect = (items) => {
    for (const item of items) {
      if (isIvaRetenidoNameMatch(item)) matches.push(item);
      if (item.children?.length) collect(item.children);
    }
  };
  collect(accounts);
  if (matches.length === 0) return null;
  const exact = matches.find((a) => a.key === IVA_RETENIDO_DEFAULT_KEY);
  if (exact) return exact;
  return matches.reduce((best, cur) =>
    accountKeySegmentCount(cur.key) > accountKeySegmentCount(best.key) ? cur : best,
  );
};

/** Obligation meta.type values that pay suppliers with corporate credit card (Proveedores / Tarjeta empresarial). */
const CREDIT_CARD_OBLIGATION_TYPES = new Set([
  'tarjetadecreditonacional',
  'tarjetadecreditointernacional',
]);

/** Obligation meta.type value for boleta honorarios retention. */
const BH_RETENTION_OBLIGATION_TYPE = 'retencionboletashonorarios';

/** Obligation meta.type: rendición (statement reconciliation) for credit cards. */
const CREDIT_CARD_RENDICION_TYPES = new Set([
  'rendiciontarjetacreditonacional',
  'rendiciontarjetacreditointernacional',
]);

/**
 * DOCUMENT contact/global/agenda account for emission preview.
 * With `document`, same resolution as Pascale (`resolveWinningDocumentRule`).
 * Without `document`, legacy contact-only lookup (no global/agenda fallback).
 */
export const getAccountFromContactRule = (
  isReceived,
  counterpartyRut,
  rules = [],
  accounts = [],
  documentSubType = null,
  document = null,
) => {
  if (isReceived === undefined || isReceived === null) return null;
  if (!rules?.length) return null;

  if (document) {
    const winner = resolveWinningDocumentRule(document, rules);
    if (!winner) return null;
    const accountKey = resolveDocumentRuleAccountKey(winner, document);
    return accountKey ? getAccountByKey(accountKey, accounts) : null;
  }

  if (!counterpartyRut) return null;

  const rutKey = normalizeRut(counterpartyRut);

  let matchingRules = rules.filter((rule) =>
    rule.ruleType === 'DOCUMENT' &&
    rule.isEnabled &&
    rule.isReceived === isReceived &&
    !rule.isGlobalDocument &&
    normalizeRut(rule.contactRut) === rutKey
  );

  if (documentSubType === 'HONORARIO') {
    matchingRules = matchingRules.filter((rule) => rule.documentSubType === 'HONORARIO');
  } else {
    matchingRules = matchingRules.filter((rule) => rule.documentSubType == null || rule.documentSubType === 'DTE');
  }

  if (matchingRules.length === 0) return null;

  matchingRules.sort((a, b) => (a.priority || 999) - (b.priority || 999));
  return getAccountByKey(matchingRules[0].accountKey, accounts);
};

/**
 * Get default journal entry structure based on transaction/document type
 * 
 * @param {Object} editData - The transaction or document data
 * @param {string} editData.originType - Type of origin: "TRANSACTION", "TAX_DOCUMENT", "OBLIGATION", "CREDIT"
 * @param {string} editData.type - Transaction type: "SPEND" or "RECIEVED" (for transactions)
 * @param {number} editData.localAmount - The amount to use for the journal entry
 * @param {Object} company - The company data (optional, for tax rules)
 * @param {boolean} company.taxRecoverable - Whether the company recovers IVA
 * @param {Array} accounts - The account chart data from context
 * @returns {Object|null} - Returns default entries structure or null if no rule applies
 */
export const getDefaultJournalEntries = (
  editData,
  company = null,
  accounts = [],
  passwordIdToAccountKeyMap = null,
  creditCardIdToAccountKeyMap = null,
) => {
  if (!editData) return null;

  const { originType, type, localAmount, support, sources } = editData;
  const amount = localAmount || 0;

  // Check if there's actually a transaction in sources
  // Prioritize sources array check (more reliable) over support.transactions
  const hasTransaction = sources?.length > 0 
    ? sources.some(s => s.type === 'TRANSACTION')
    : (support?.transactions?.length > 0 && support.transactions.some(t => !t.documentType));
  
  // Get transaction type from support.transactions if available
  const transaction = support?.transactions?.[0];
  const transactionType = transaction?.type || type;

  const obligationForCardRule = support?.documents?.find((d) => d?.documentType === 'OBLIGATION');
  const creditCardObType = obligationForCardRule?.meta?.type;

  // Pay corporate credit card from bank: match includes a TRANSACTION + obligation tarjeta (nacional/internacional).
  // DEBE 2.01.01.03 Tarjeta de crédito empresarial | HABER 1.02.01.01 Banco
  if (
    obligationForCardRule &&
    creditCardObType &&
    CREDIT_CARD_OBLIGATION_TYPES.has(creditCardObType) &&
    hasTransaction &&
    amount > 0
  ) {
    const corporateCardAccount = getCompanyAccountByTag('TARJETA_CREDITO', accounts);
    const bankAccount = getBankAccountForTransaction(transaction, accounts, passwordIdToAccountKeyMap);
    return {
      entries: [
        {
          ranking: 1,
          account: corporateCardAccount,
          debit: amount > 0 ? amount : 0,
          credit: 0,
        },
        {
          ranking: 2,
          account: bankAccount,
          debit: 0,
          credit: amount > 0 ? amount : 0,
        },
      ],
    };
  }

  // Rule 1: Transaction type RECIEVED (Income)
  // Row 1: DEBE - Cash/Bank account (1.02.01.01)
  // Row 2: HABER - Revenue/Accounts Receivable (to be filled by user)
  if (originType === 'TRANSACTION' && transactionType === TransactionType.RECIEVED) {
    const bankAccount = getBankAccountForTransaction(transaction, accounts, passwordIdToAccountKeyMap);
    return {
      entries: [
        {
          ranking: 1,
          account: bankAccount, // Full account object
          debit: amount > 0 ? amount : 0,
          credit: 0,
        },
        {
          ranking: 2,
          account: null, // Revenue/AR account - to be filled by user
          debit: 0,
          credit: amount > 0 ? amount : 0,
        }
      ]
    };
  }

  // Rule 2: Transaction type SPEND (Expense)
  // Row 1: DEBE - Expense account (to be filled by user)
  // Row 2: HABER - Cash/Bank account (1.02.01.01)
  if (originType === 'TRANSACTION' && transactionType === TransactionType.SPEND) {
    const bankAccount = getBankAccountForTransaction(transaction, accounts, passwordIdToAccountKeyMap);
    return {
      entries: [
        {
          ranking: 1,
          account: null, // Expense account - to be filled by user
          debit: amount > 0 ? amount : 0,
          credit: 0,
        },
        {
          ranking: 2,
          account: bankAccount, // Full account object
          debit: 0,
          credit: amount > 0 ? amount : 0,
        }
      ]
    };
  }

  // Credit card obligations (national / international), no bank row in match: reduce accounts payable, recognize corporate card liability.
  // DEBE 2.04.01.01 Proveedores | HABER 2.01.01.03 Tarjeta de crédito empresarial
  if (
    obligationForCardRule &&
    creditCardObType &&
    CREDIT_CARD_OBLIGATION_TYPES.has(creditCardObType) &&
    !hasTransaction &&
    amount > 0
  ) {
    const accountsPayableAccount = getCompanyAccountByTag('PROVEEDORES', accounts);
    const corporateCardAccount = getCompanyAccountByTag('TARJETA_CREDITO', accounts);
    return {
      entries: [
        {
          ranking: 1,
          account: accountsPayableAccount,
          debit: amount > 0 ? amount : 0,
          credit: 0,
        },
        {
          ranking: 2,
          account: corporateCardAccount,
          debit: 0,
          credit: amount > 0 ? amount : 0,
        },
      ],
    };
  }

  // Rule 3: OBLIGATION, TAX_DOCUMENT, or CREDIT with transaction type RECIEVED
  // When a document/obligation is matched with a RECIEVED transaction
  // Only apply bank account rule if there's an actual transaction in sources
  // Row 1: DEBE - Cash/Bank account (1.02.01.01)
  // Row 2: HABER - Expense/Payable account (to be filled by user)
  if ((originType === 'OBLIGATION' || originType === 'TAX_DOCUMENT' || originType === 'CREDIT') 
      && transactionType === TransactionType.RECIEVED
      && hasTransaction) {
    const bankAccount = getBankAccountForTransaction(transaction, accounts, passwordIdToAccountKeyMap);
    return {
      entries: [
        {
          ranking: 1,
          account: bankAccount, // Full account object
          debit: amount > 0 ? amount : 0,
          credit: 0,
        },
        {
          ranking: 2,
          account: null, // Expense/Payable account - to be filled by user
          debit: 0,
          credit: amount > 0 ? amount : 0,
        }
      ]
    };
  }

  // Rule 4: OBLIGATION, TAX_DOCUMENT, or CREDIT with transaction type SPEND
  // When a document/obligation is matched with a SPEND transaction
  // Only apply bank account rule if there's an actual transaction in sources
  // Row 1: DEBE - Expense/Payable account (to be filled by user)
  // Row 2: HABER - Cash/Bank account (1.02.01.01)
  if ((originType === 'OBLIGATION' || originType === 'TAX_DOCUMENT' || originType === 'CREDIT') 
      && transactionType === TransactionType.SPEND
      && hasTransaction) {
    const bankAccount = getBankAccountForTransaction(transaction, accounts, passwordIdToAccountKeyMap);
    return {
      entries: [
        {
          ranking: 1,
          account: null, // Expense/Payable account - to be filled by user
          debit: amount > 0 ? amount : 0,
          credit: 0,
        },
        {
          ranking: 2,
          account: bankAccount, // Full account object
          debit: 0,
          credit: amount > 0 ? amount : 0,
        }
      ]
    };
  }

  // Rule 5: TAX_DOCUMENT with single DOCUMENT source (no transaction)
  // Special handling for tax documents (invoices) with tax breakdown
  const document = support?.documents?.[0];
  const isSingleDocumentSource = sources?.length === 1 && sources[0]?.type === 'DOCUMENT';
  const isTaxDocument = originType === 'TAX_DOCUMENT' || document?.documentType === 'TAX_DOCUMENT';
  
  if (isSingleDocumentSource && isTaxDocument && !hasTransaction && document) {
    const isReceived = document.meta?.isReceived === true;
    const taxAmount = Number(document.meta?.taxAmount) || 0;
    const commonTaxes = Number(document.meta?.commonTaxes) || 0;
    const taxLineAmount = taxAmount + commonTaxes;
    const netAmount = Number(document.meta?.netAmount) || 0;
    const otherTaxes = document.meta?.otherTaxes;
    const hasOtherTaxes = otherTaxes && Array.isArray(otherTaxes) && otherTaxes.length > 0;
    const otherTaxesAmount = hasOtherTaxes
      ? otherTaxes.reduce((sum, tax) => sum + (tax.value || 0), 0)
      : 0;
    const fullAmount = amount || (taxLineAmount + netAmount + otherTaxesAmount);
    const documentCode = document.meta?.documentCode;
    
    // Rule 5a: Boleta de honorarios (liquid amount only; retention is separate OBLIGATION)
    // DEBE: User selects account
    // HABER: 2.03.02.01
    if (documentCode != null && HONORARIO_DOCUMENT_CODES.includes(documentCode)) {
      const honorariosAmount = Number(netAmount) || 0;
      const honorariosCreditAccount = getCompanyAccountByTag('HONORARIOS_POR_PAGAR', accounts);

      return {
        entries: [
          {
            ranking: 1,
            account: null, // User selects (debit)
            debit: honorariosAmount > 0 ? honorariosAmount : 0,
            credit: 0,
          },
          {
            ranking: 2,
            account: honorariosCreditAccount, // 2.03.02.01 (credit)
            debit: 0,
            credit: honorariosAmount > 0 ? honorariosAmount : 0,
          },
        ],
      };
    }

    // Rule 5b: Nota de Crédito (documentCode 61, 112): reverse of sales - Credit receivable, Debit tax, Debit revenue
    if (!isReceived && documentCode != null && CREDIT_NOTE_DOCUMENT_CODES.includes(documentCode)) {
      const accountsReceivableAccount = resolveSalesReceivableAccount(accounts, documentCode);
      const taxPayableAccount = getCompanyAccountByTag('IVA_DEBITO', accounts);
      const expenseAccount = resolveSalesRevenueAccount(accounts, documentCode);

      const entries = [
        {
          ranking: 1,
          account: accountsReceivableAccount,
          debit: 0,
          credit: fullAmount > 0 ? fullAmount : 0,
        }
      ];

      let currentRanking = 2;
      const hasTaxAmount = taxLineAmount > 0;

      if (hasTaxAmount) {
        entries.push({
          ranking: currentRanking,
          account: taxPayableAccount,
          debit: taxLineAmount,
          credit: 0,
        });
        currentRanking++;
      }

      if (hasOtherTaxes && otherTaxesAmount > 0) {
        // Other taxes use same account as expense (net) row; if not defined, empty
        entries.push({
          ranking: currentRanking,
          account: null,
          debit: otherTaxesAmount,
          credit: 0,
        });
        currentRanking++;
      }

      entries.push({
        ranking: currentRanking,
        account: expenseAccount,
        debit: netAmount > 0 ? netAmount : 0,
        credit: 0,
      });

      return { entries };
    }

    // Rule 5b-compra: Nota de Crédito compra (61, 112): reverse of purchase - Credit tax, Credit expense, Debit AP
    if (isReceived && documentCode != null && CREDIT_NOTE_DOCUMENT_CODES.includes(documentCode)) {
      const accountsPayableAccount = getCompanyAccountByTag('PROVEEDORES', accounts);
      const taxReceivableAccount = company?.taxRecoverable === false
        ? getCompanyAccountByTag('IVA_NO_RECUPERABLE', accounts)
        : getCompanyAccountByTag('IVA_CREDITO', accounts);

      const entries = [];
      const hasTaxAmount = taxLineAmount > 0;

      if (hasTaxAmount) {
        entries.push({
          ranking: 1,
          account: taxReceivableAccount,
          debit: 0,
          credit: taxLineAmount,
        });
      }

      let currentRanking = hasTaxAmount ? 2 : 1;

      if (hasOtherTaxes && otherTaxesAmount > 0) {
        entries.push({
          ranking: currentRanking,
          account: null,
          debit: 0,
          credit: otherTaxesAmount,
        });
        currentRanking++;
      }

      entries.push({
        ranking: currentRanking,
        account: null,
        debit: 0,
        credit: netAmount > 0 ? netAmount : 0,
      });
      currentRanking++;

      entries.push({
        ranking: currentRanking,
        account: accountsPayableAccount,
        debit: fullAmount > 0 ? fullAmount : 0,
        credit: 0,
      });

      return { entries };
    }

    const isDte46WithIvaRetenido =
      isReceived &&
      isFacturaCompraDocumentCode(documentCode) &&
      hasOtherTaxes &&
      otherTaxesAmount > 0 &&
      otherTaxes.some(isOtherTaxIvaRetenido);

    // Rule 5c: DTE 46 compra con IVA retenido (AP = neto; Cr IVA retenido)
    if (isDte46WithIvaRetenido) {
      const accountsPayableAccount = getCompanyAccountByTag('PROVEEDORES', accounts);
      const taxReceivableAccount = company?.taxRecoverable === false
        ? getCompanyAccountByTag('IVA_NO_RECUPERABLE', accounts)
        : getCompanyAccountByTag('IVA_CREDITO', accounts);
      const ivaRetenidoAccount =
        findIvaRetenidoAccount(accounts) || getCompanyAccountByTag('IVA_RETENIDO_CREDITO', accounts);
      const entries = [];
      let currentRanking = 1;
      const hasTaxAmount = taxLineAmount > 0;

      if (hasTaxAmount) {
        entries.push({
          ranking: currentRanking,
          account: taxReceivableAccount,
          debit: taxLineAmount,
          credit: 0,
        });
        currentRanking++;
      }

      entries.push({
        ranking: currentRanking,
        account: ivaRetenidoAccount,
        debit: 0,
        credit: otherTaxesAmount,
      });
      currentRanking++;

      entries.push({
        ranking: currentRanking,
        account: null,
        debit: netAmount > 0 ? netAmount : 0,
        credit: 0,
      });
      currentRanking++;

      entries.push({
        ranking: currentRanking,
        account: accountsPayableAccount,
        debit: 0,
        credit: netAmount > 0 ? netAmount : 0,
      });

      return { entries };
    }
    
    if (isReceived) {
      // TAX_DOCUMENT received (purchase invoice)
      // HABER: Accounts Payable (2.04.01.01) for full amount
      // DEBE: Multiple accounts:
      //   1. Tax Receivable account - depends on company.taxRecoverable:
      //      - If company recovers IVA (taxRecoverable === true): 1.08.02.01 (IVA Crédito)
      //      - If company does NOT recover IVA (taxRecoverable === false): 3.15.02.01 (Otros Impuestos)
      //   2. Other Taxes (if meta.otherTaxes exists): same account as expense (3); if not defined, empty
      //   3. User selects account for netAmount
      const accountsPayableAccount = getCompanyAccountByTag('PROVEEDORES', accounts);
      
      // Determine which tax account to use based on company's taxRecoverable setting
      const taxReceivableAccount = company?.taxRecoverable === false
        ? getCompanyAccountByTag('IVA_NO_RECUPERABLE', accounts)
        : getCompanyAccountByTag('IVA_CREDITO', accounts);
      
      // Build entries array - start with accounts receivable
      const entries = [];
      
      // Only add tax account entry if tax line > 0 (taxAmount + commonTaxes)
      const hasTaxAmount = taxLineAmount > 0;
      if (hasTaxAmount) {
        entries.push({
          ranking: 1,
          account: taxReceivableAccount, // Tax account (1.08.02.01 or 3.15.02.01 based on taxRecoverable)
          debit: taxLineAmount,
          credit: 0,
        });
      }
      
      // Track current ranking for subsequent entries
      let currentRanking = hasTaxAmount ? 2 : 1;
      
      // Add otherTaxes entry if it exists (not for DTE 46 IVA retenido — handled in Rule 5c)
      if (hasOtherTaxes && otherTaxesAmount > 0 && !isDte46WithIvaRetenido) {
        entries.push({
          ranking: currentRanking,
          account: null,
          debit: otherTaxesAmount,
          credit: 0,
        });
        currentRanking++;
      }
      
      // Add net amount entry (user selects account)
      entries.push({
        ranking: currentRanking,
        account: null, // User selects account for net amount
        debit: netAmount > 0 ? netAmount : 0,
        credit: 0,
      });
      currentRanking++;
      
      // Add accounts payable entry
      entries.push({
        ranking: currentRanking,
        account: accountsPayableAccount, // Accounts Payable (2.04.01.01)
        debit: 0,
        credit: fullAmount > 0 ? fullAmount : 0,
      });
      
      return { entries };
    } else {
      // TAX_DOCUMENT not received (sales invoice/income)
      // HABER: Multiple accounts:
      //   1. Tax Payable (2.06.02.01) for taxAmount
      //   2. Other Taxes account (if meta.otherTaxes exists) - depends on company.taxAdditional:
      //      - If company.taxAdditional === true: Use company.taxAdditionalAccount (from Configuracion2)
      //      - If company.taxAdditional === false: User selects account for otherTaxes
      //   3. Revenue (4.01.01.01) for netAmount
      // DEBE: Accounts Receivable (1.05.01.01) for full amount
      const accountsReceivableAccount = resolveSalesReceivableAccount(accounts, documentCode);
      const taxPayableAccount = getCompanyAccountByTag('IVA_DEBITO', accounts);
      const expenseAccount = resolveSalesRevenueAccount(accounts, documentCode);
      
      // Build entries array - start with accounts receivable
      const entries = [
        {
          ranking: 1,
          account: accountsReceivableAccount, // Accounts Receivable (1.05.01.01)
          debit: fullAmount > 0 ? fullAmount : 0,
          credit: 0,
        }
      ];
      
      // Track current ranking for subsequent entries
      let currentRanking = 2;
      
      // Only add tax payable entry if tax line > 0 (taxAmount + commonTaxes)
      const hasTaxAmount = taxLineAmount > 0;
      if (hasTaxAmount) {
        entries.push({
          ranking: currentRanking,
          account: taxPayableAccount, // Tax Payable (2.06.02.01)
          debit: 0,
          credit: taxLineAmount,
        });
        currentRanking++;
      }
      
      // Add otherTaxes entry if it exists (same account as expense/revenue row; if not defined, empty)
      if (hasOtherTaxes && otherTaxesAmount > 0) {
        entries.push({
          ranking: currentRanking,
          account: null,
          debit: 0,
          credit: otherTaxesAmount,
        });
        currentRanking++;
      }
      
      // Add revenue entry
      entries.push({
        ranking: currentRanking,
        account: expenseAccount, // Expense/Revenue (4.01.01.01)
        debit: 0,
        credit: netAmount > 0 ? netAmount : 0,
      });
      
      return { entries };
    }
  }

  // Rule 6: Document without transaction (TAX_DOCUMENT, CREDIT, OBLIGATION)
  // When a document exists without a matched transaction, just fill amounts
  // No bank account - user must select appropriate accounts
  // Row 1: DEBE - Account to be filled by user
  // Row 2: HABER - Account to be filled by user
  if ((originType === 'OBLIGATION' || originType === 'TAX_DOCUMENT' || originType === 'CREDIT') 
      && !hasTransaction
      && amount > 0) {
    const obligationDocument =
      support?.documents?.find((d) => d?.documentType === 'OBLIGATION') || document;
    const obligationType = String(obligationDocument?.meta?.type || '').toLowerCase();
    const isBHRetention = obligationType === BH_RETENTION_OBLIGATION_TYPE;

    if (originType === 'OBLIGATION' && isBHRetention) {
      const bhRetentionDebitAccount = getCompanyAccountByTag('GASTOS_DEFAULT', accounts);
      const bhRetentionCreditAccount = getCompanyAccountByTag('RETENCION_BH', accounts);
      return {
        entries: [
          {
            ranking: 1,
            account: bhRetentionDebitAccount,
            debit: amount > 0 ? amount : 0,
            credit: 0,
          },
          {
            ranking: 2,
            account: bhRetentionCreditAccount,
            debit: 0,
            credit: amount > 0 ? amount : 0,
          }
        ]
      };
    }

    if (originType === 'OBLIGATION' && CREDIT_CARD_RENDICION_TYPES.has(obligationType) && amount > 0) {
      const periodStr =
        support?.documents?.[0]?.meta?.period ??
        support?.documents?.[0]?.meta?.flexiblePeriod ??
        obligationDocument?.meta?.period ??
        obligationDocument?.meta?.flexiblePeriod ??
        '';
      const cardId = parseCreditCardIdFromPeriod(periodStr);
      const creditCardAccountKey = resolveCreditCardAccountKey(cardId, accounts, undefined, creditCardIdToAccountKeyMap);
      const creditCardAccount = getAccountByKey(creditCardAccountKey, accounts);
      return {
        entries: [
          {
            ranking: 1,
            account: null,
            debit: amount > 0 ? amount : 0,
            credit: 0,
          },
          {
            ranking: 2,
            account: creditCardAccount,
            debit: 0,
            credit: amount > 0 ? amount : 0,
          },
        ],
      };
    }

    // For documents without transactions, fill balanced amounts but leave accounts empty
    // TAX_DOCUMENT/CREDIT (sales): DEBE = Accounts Receivable, HABER = Revenue
    // OBLIGATION (expenses): DEBE = Expense, HABER = Accounts Payable
    // User will select the appropriate accounts
    const isIncome = originType === 'TAX_DOCUMENT' || originType === 'CREDIT';
    
    return {
      entries: [
        {
          ranking: 1,
          account: null, // To be filled by user (e.g., Accounts Receivable for sales, Expense for obligations)
          debit: amount > 0 ? amount : 0,
          credit: 0,
        },
        {
          ranking: 2,
          account: null, // To be filled by user (e.g., Revenue for sales, Accounts Payable for obligations)
          debit: 0,
          credit: amount > 0 ? amount : 0,
        }
      ]
    };
  }

  // No rule applies
  return null;
};

/**
 * Recursively search for an account by key in the hierarchical plan de cuentas data
 * 
 * @param {string} accountKey - The account key (e.g., "1.02.01.01")
 * @param {Array} accounts - The plan de cuentas data from context
 * @returns {Object|null} - Full account object with key, label, name, category, or null if not found
 */
export const getAccountByKey = (accountKey, accounts = []) => {
  if (!accountKey || !accounts?.length) return null;

  const searchRecursive = (items) => {
    for (const item of items) {
      if (item.key === accountKey) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = searchRecursive(item.children);
        if (found) return found;
      }
    }
    return null;
  };

  return searchRecursive(accounts);
};

/**
 * Normalize document.accountKey from Parametrización (null / '' / 'none' => null).
 * Keep in sync with server/src/pascale/logic/documentAccountKey.logic.ts
 */
export const getDocumentParametrizationAccountKey = (document) => {
  const raw = document?.accountKey;
  if (raw == null) return null;
  const key = String(raw).trim();
  if (!key || key === 'none') return null;
  return key;
};

/**
 * Apply an account to the primary (net) emission row and matching otherTaxes row.
 *
 * @param {Array} entries - Journal entry rows from cheatSheet defaults
 * @param {Object} document - Document with meta (netAmount, documentCode, isReceived, otherTaxes)
 * @param {Object} account - Full account object to assign
 * @param {Object} [options]
 * @param {boolean} [options.replaceExistingAccount=false] - When true, overwrite account on the primary net row
 * @returns {{ entries: Array, applied: boolean }}
 */
export const applyAccountToEmissionEntries = (
  entries,
  document,
  account,
  { replaceExistingAccount = false } = {},
) => {
  if (!account || !entries?.length) {
    return { entries: entries || [], applied: false };
  }

  const updatedEntries = entries.map((e) => ({ ...e }));
  const isReceived = document?.meta?.isReceived === true;
  const documentCode = document?.meta?.documentCode;
  const isCreditNote = documentCode != null && CREDIT_NOTE_DOCUMENT_CODES.includes(documentCode);
  const isPurchaseCreditNote = isReceived === true && isCreditNote;
  const eps = 0.01;
  const netAmount = Number(document?.meta?.netAmount) || 0;
  const rowEligible = (e) => replaceExistingAccount || !e.account;
  let applied = false;

  // Prefer the NET line for both directions (avoids accidentally filling otherTaxes, etc).
  if (netAmount > 0) {
    if (isReceived === true) {
      const idx = updatedEntries.findIndex((e) => {
        if (!rowEligible(e)) return false;
        if (isPurchaseCreditNote) {
          const credit = Number(e.credit) || 0;
          return credit > 0 && Math.abs(credit - netAmount) <= eps;
        }
        const debit = Number(e.debit) || 0;
        return debit > 0 && Math.abs(debit - netAmount) <= eps;
      });
      if (idx !== -1) {
        updatedEntries[idx].account = account;
        applied = true;
      }
    } else if (isReceived === false) {
      const idx = updatedEntries.findIndex((e) => {
        if (!rowEligible(e)) return false;
        if (isCreditNote) {
          const debit = Number(e.debit) || 0;
          return debit > 0 && Math.abs(debit - netAmount) <= eps;
        }
        const credit = Number(e.credit) || 0;
        return credit > 0 && Math.abs(credit - netAmount) <= eps;
      });
      if (idx !== -1) {
        updatedEntries[idx].account = account;
        applied = true;
      }
    }
  }

  if (!applied) {
    for (const entry of updatedEntries) {
      if (!rowEligible(entry)) continue;
      const debit = Number(entry.debit) || 0;
      const credit = Number(entry.credit) || 0;
      const needsDebitPurchase = isReceived === true && !isCreditNote && debit > 0;
      const needsCreditPurchaseNC = isPurchaseCreditNote && credit > 0;
      const needsCredit = isReceived === false && !isCreditNote && credit > 0;
      const needsDebitSalesNC = isReceived === false && isCreditNote && debit > 0;

      if (needsDebitPurchase || needsCreditPurchaseNC || needsCredit || needsDebitSalesNC) {
        entry.account = account;
        applied = true;
        break;
      }
    }
  }

  // Contact-rule style: for sales, allow replacing the default revenue row even when
  // replaceExistingAccount is false (legacy behavior when net row already has INGRESOS).
  if (!applied && !replaceExistingAccount && isReceived === false && document?.meta && netAmount > 0) {
    const idx = updatedEntries.findIndex((e) => {
      if (isCreditNote) {
        const debit = Number(e.debit) || 0;
        return debit > 0 && Math.abs(debit - netAmount) <= eps;
      }
      const credit = Number(e.credit) || 0;
      return credit > 0 && Math.abs(credit - netAmount) <= eps;
    });
    if (idx !== -1) {
      updatedEntries[idx].account = account;
      applied = true;
    }
  }

  if (!applied && !replaceExistingAccount && isReceived === true && document?.meta && netAmount > 0) {
    const idx = updatedEntries.findIndex((e) => {
      if (isPurchaseCreditNote) {
        const credit = Number(e.credit) || 0;
        return credit > 0 && Math.abs(credit - netAmount) <= eps;
      }
      const debit = Number(e.debit) || 0;
      return debit > 0 && Math.abs(debit - netAmount) <= eps;
    });
    if (idx !== -1) {
      updatedEntries[idx].account = account;
      applied = true;
    }
  }

  const otherTaxes = document?.meta?.otherTaxes;
  const hasOtherTaxes = otherTaxes && Array.isArray(otherTaxes) && otherTaxes.length > 0;
  const otherTaxesAmount = hasOtherTaxes
    ? otherTaxes.reduce((sum, tax) => sum + (tax.value || 0), 0)
    : 0;
  if (applied && otherTaxesAmount > 0) {
    const otherIdx = updatedEntries.findIndex((e) => {
      if (e.account) return false;
      const debit = Number(e.debit) || 0;
      const credit = Number(e.credit) || 0;
      if (isPurchaseCreditNote) {
        return credit > 0 && Math.abs(credit - otherTaxesAmount) <= eps && debit === 0;
      }
      if (isReceived && !isCreditNote) {
        return debit > 0 && Math.abs(debit - otherTaxesAmount) <= eps && credit === 0;
      }
      if (!isReceived && isCreditNote) {
        return debit > 0 && Math.abs(debit - otherTaxesAmount) <= eps && credit === 0;
      }
      return credit > 0 && Math.abs(credit - otherTaxesAmount) <= eps && debit === 0;
    });
    if (otherIdx !== -1) {
      updatedEntries[otherIdx].account = account;
    }
  }

  return { entries: updatedEntries, applied };
};

/**
 * Apply accounting rules to journal entry form data
 * 
 * @param {Object} editData - The transaction or document data
 * @param {Object} company - The company data (optional, for tax rules)
 * @param {Array} accounts - The account chart data from context
 * @returns {Object} - Updated form data with default entries applied
 */
export const applyAccountingRules = (
  editData,
  company = null,
  accounts = [],
  rules = [],
  passwordIdToAccountKeyMap = null,
  creditCardIdToAccountKeyMap = null,
) => {
  const sources = editData?.sources || [];
  const computedEntryType =
    editData?.entryType ||
    (editData?.origin === 'MATCH' ? 'matchEntry' : (sources?.length > 1 ? 'matchEntry' : 'emissionEntry'));

  // Always start from the hardcoded defaults (these set debit/credit correctly).
  const defaultEntries = getDefaultJournalEntries(
    editData,
    company,
    accounts,
    passwordIdToAccountKeyMap,
    creditCardIdToAccountKeyMap,
  );

  if (!defaultEntries) {
    // No rule applies, return default empty structure
    return {
      entries: [
        {
          ranking: 1,
          account: null,
          debit: 0,
          credit: 0,
        },
        {
          ranking: 2,
          account: null,
          debit: 0,
          credit: 0,
        }
      ]
    };
  }

  // 1) MATCH rules apply ONLY to matchEntry (precedence over cheatSheet defaults)
  if (computedEntryType === 'matchEntry' && rules?.length && editData) {
    const transactionType = editData.type;

    // Match rules use `meta.type` (e.g. "otrosingresos")
    const documents = editData.support?.documents || [];
    const docWithMetaType =
      documents.find((d) => d?.meta?.type) ||
      documents.find((d) => d?.meta?.typeLabel) ||
      null;

    const obligationDoc =
      documents.find(
        (d) =>
          d?.documentType === 'OBLIGATION' &&
          documentMetaType(d) === MATCH_DOCUMENT_TYPE_CAPITAL_CUOTA,
      ) ||
      documents.find((d) => d?.documentType === 'OBLIGATION') ||
      docWithMetaType;

    const agendaIdsForMatchRule = [
      ...new Set([
        ...collectAgendaIdsForMatchRule(obligationDoc),
        ...collectAgendaIdsForMatchRule(docWithMetaType),
      ]),
    ];

    const typeKey =
      obligationDoc?.meta?.type ||
      docWithMetaType?.meta?.type ||
      editData.support?.type ||
      editData.typeKey ||
      null;

    if (transactionType && typeKey) {
      const txForAmount = resolveTransactionForBankAccount(editData);
      const ruleAccount = getAccountFromRule(
        transactionType,
        typeKey || MATCH_RULE_CATCH_ALL_TYPE,
        rules,
        accounts,
        agendaIdsForMatchRule.length ? agendaIdsForMatchRule : undefined,
        obligationDoc,
        { supportDocuments: documents, transaction: txForAmount },
      );
      if (ruleAccount) {
        const amount =
          positiveAbs(editData.localAmount) ||
          positiveAbs(editData.localValue) ||
          resolveMatchEntryAmountClp(editData, txForAmount) ||
          resolveTransactionEntryAmountClp(txForAmount);
        const bankAccount = getBankAccountForTransaction(
          resolveTransactionForBankAccount(editData),
          accounts,
          passwordIdToAccountKeyMap,
        );

        if (transactionType === TransactionType.RECIEVED) {
          return {
            entries: [
              { ranking: 1, account: bankAccount, debit: amount > 0 ? amount : 0, credit: 0 },
              { ranking: 2, account: ruleAccount, debit: 0, credit: amount > 0 ? amount : 0 },
            ],
          };
        }
        if (transactionType === TransactionType.SPEND) {
          return {
            entries: [
              { ranking: 1, account: ruleAccount, debit: amount > 0 ? amount : 0, credit: 0 },
              { ranking: 2, account: bankAccount, debit: 0, credit: amount > 0 ? amount : 0 },
            ],
          };
        }
      }
    }
  }

  // 2) emissionEntry: document parametrization (accountKey) wins over contact/RUT rules
  if (computedEntryType === 'emissionEntry' && editData) {
    const document = editData.support?.documents?.[0] || null;
    const baseRows = (defaultEntries.entries || []).map((e) => ({ ...e }));

    const parametrizationKey = getDocumentParametrizationAccountKey(document);
    const parametrizationAccount = parametrizationKey
      ? getAccountByKey(parametrizationKey, accounts)
      : null;

    if (parametrizationAccount) {
      const { entries: withParam, applied } = applyAccountToEmissionEntries(
        baseRows,
        document,
        parametrizationAccount,
        { replaceExistingAccount: true },
      );
      if (applied) {
        return { ...defaultEntries, entries: withParam };
      }
    }

    // Fallback: DOCUMENT contact rules (only when no valid parametrized account)
    if (rules?.length && !parametrizationAccount) {
      const isReceived = document?.meta?.isReceived === true;
      const counterpartyRut = isReceived ? document?.meta?.issuerId : document?.meta?.receiverId;
      const documentCode = document?.meta?.documentCode;
      const documentSubType =
        documentCode != null && HONORARIO_DOCUMENT_CODES.includes(documentCode) ? 'HONORARIO' : 'DTE';

      const ruleAccount = getAccountFromContactRule(
        isReceived,
        counterpartyRut,
        rules,
        accounts,
        documentSubType,
        document,
      );
      if (ruleAccount) {
        const { entries: withRule, applied } = applyAccountToEmissionEntries(
          baseRows,
          document,
          ruleAccount,
          { replaceExistingAccount: false },
        );
        if (applied) {
          return { ...defaultEntries, entries: withRule };
        }
      }
    }
  }

  if (computedEntryType === 'emissionEntry' && isPascaleBasicMode(company)) {
    const doc = editData.support?.documents?.[0] || editData;
    const consolidated = buildConsolidatedHonorariosJournalEntries(doc, company, accounts);
    if (consolidated) {
      return { entries: consolidated };
    }
  }

  return defaultEntries;
};

